/**
 * Vyron Collection AJAX Filtering and Pagination Module
 * Adheres to SOLID Principles
 */

// 1. Single Responsibility Principle: Debouncer Class
class CollectionDebouncer {
  constructor(delay = 300) {
    this.delay = delay;
    this.timeout = null;
  }

  execute(callback) {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(callback, this.delay);
  }
}

// 2. Single Responsibility Principle: State management
class CollectionState {
  constructor() {
    this.params = new URLSearchParams(window.location.search);
  }

  setFilter(key, value) {
    if (!value || value === 'all') {
      this.params.delete(key);
    } else {
      this.params.set(key, value);
    }
    // Always reset page to 1 when filters change
    this.params.delete('page');
  }

  setSort(value) {
    if (!value || value === 'featured') {
      this.params.delete('sort_by');
    } else {
      this.params.set('sort_by', value);
    }
    // Reset page to 1 on sort change
    this.params.delete('page');
  }

  setPage(page) {
    if (page && page > 1) {
      this.params.set('page', page);
    } else {
      this.params.delete('page');
    }
  }

  getParam(key) {
    return this.params.get(key) || '';
  }

  getPage() {
    const pageVal = parseInt(this.params.get('page'), 10);
    return isNaN(pageVal) || pageVal < 1 ? 1 : pageVal;
  }

  buildQueryString() {
    const search = this.params.toString();
    return search ? `?${search}` : '';
  }

  syncFromUrl() {
    this.params = new URLSearchParams(window.location.search);
  }

  clearAll() {
    this.params = new URLSearchParams();
  }
}

// 3. Single Responsibility Principle: API Fetching
class CollectionAPI {
  async fetchCollectionProducts(handle) {
    try {
      // Fetch up to 250 products in one request
      const response = await fetch(`/collections/${handle}/products.json?limit=250`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.products || [];
    } catch (error) {
      console.error('Collection API Fetch Error:', error);
      throw error;
    }
  }
}

// 4. Single Responsibility Principle: DOM Rendering & Updating
class CollectionDOM {
  constructor() {
    this.grid = document.getElementById('vr-collection-grid');
    this.paginationContainer = document.getElementById('vr-pagination-container');
    this.countElement = document.getElementById('vr-collection-count');
    
    // Automatically detect shop currency symbol from initial product card price if present
    this.currencySymbol = '$';
    const firstPriceEl = document.querySelector('.vr-collection__card-price');
    if (firstPriceEl) {
      const text = firstPriceEl.textContent.trim();
      const match = text.match(/^([^\d\s,]+)/);
      if (match) {
        this.currencySymbol = match[1];
      }
    }
  }

  harvestPreRenderedBadges() {
    window.__collectionBadges = window.__collectionBadges || {};
    if (this.grid) {
      const cards = this.grid.querySelectorAll('.vr-collection__card');
      cards.forEach(card => {
        const id = card.getAttribute('data-product-id');
        const badgeEl = card.querySelector('.vr-collection__badge--custom');
        if (id && badgeEl) {
          const txt = badgeEl.textContent.trim();
          if (txt) {
            window.__collectionBadges[id] = txt;
          }
        }
      });
    }
  }

  showLoading() {
    if (this.grid) {
      this.grid.classList.add('is-loading');
    }
  }

  hideLoading() {
    if (this.grid) {
      this.grid.classList.remove('is-loading');
    }
  }

  escapeHtml(string) {
    return String(string).replace(/[&<>"']/g, function (s) {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      }[s];
    });
  }

formatPrice(priceVal) {
  const num = Number(priceVal);
  const formatted = Number.isInteger(num) ? num.toString() : num.toFixed(2);
  return `${this.currencySymbol}${formatted}`;
}

  renderProducts(products) {
    if (!this.grid) return;

    if (products.length === 0) {
      this.grid.innerHTML = `
        <div class="vr-collection__no-products">
          <p>No products found in this collection.</p>
        </div>
      `;
      return;
    }

    const cardsHtml = products.map(product => {
      // Find Option for Color/Colour/Band
      const colorOption = product.options.find(opt => {
        const name = opt.name.toLowerCase();
        return name.includes('color') || name.includes('colour') || name.includes('band');
      });

      let displayColor = '';
      if (colorOption && product.variants.length > 0) {
        const colorIndex = product.options.indexOf(colorOption);
        const optionKey = `option${colorIndex + 1}`;
        displayColor = product.variants[0][optionKey] || '';
      }
      if (!displayColor && product.variants.length > 0) {
        displayColor = product.variants[0].title;
      }

      // Format Price
      let priceText = '';
      if (product.variants.length > 0) {
        const priceVal = parseFloat(product.variants[0].price);
        priceText = isNaN(priceVal) ? '' : this.formatPrice(priceVal);
      }

      // Image
      const imgSrc = product.images.length > 0 ? product.images[0].src : '';
      const hoverImage = product.images.length > 0 ? product.images[1]?.src : product.images[0]?.src ;
      const imgAlt = product.images.length > 0 ? product.images[0].alt : product.title;


      // Resolve custom badge (custom.badge metafield) from registry, metafields, or product properties
      let customBadge = '';
      if (window.__collectionBadges && (window.__collectionBadges[product.id] || window.__collectionBadges[product.handle])) {
        customBadge = window.__collectionBadges[product.id] || window.__collectionBadges[product.handle];
      } else if (product.metafields && (product.metafields.custom?.badge || product.metafields.badge)) {
        const bm = product.metafields.custom?.badge || product.metafields.badge;
        customBadge = typeof bm === 'object' ? (bm.value || bm.name || bm.title || (Array.isArray(bm) ? bm[0] : '')) : bm;
      } else if (product.metafield && (product.metafield.custom?.badge || product.metafield.badge)) {
        const bm = product.metafield.custom?.badge || product.metafield.badge;
        customBadge = typeof bm === 'object' ? (bm.value || bm.name || bm.title || (Array.isArray(bm) ? bm[0] : '')) : bm;
      } else if (product.badge) {
        customBadge = typeof product.badge === 'object' ? (product.badge.value || product.badge.name || '') : product.badge;
      }

      const customBadgeHtml = customBadge ? `<span class="vr-collection__badge vr-collection__badge--variant vr-collection__badge--custom">${this.escapeHtml(customBadge)}</span>` : '';

      return `
        <article class="vr-collection__card" data-product-id="${product.id}">
          <div class="vr-collection__card-img-wrap">
            ${customBadgeHtml}
          ${ product.tags && product.tags.length > 0 ? `<span class="vr-collection__badge vr-collection__badge--movement">${this.escapeHtml(product.tags[0])}</span>` : '' }
            <figure class="vr-collection__figure">
            ${ `
                <img
                  src="${hoverImage ? hoverImage : imgSrc}"
                  width="auto"
                  height="auto"
                  alt="${this.escapeHtml(imgAlt)}"
                  class="vr-collection__card-img vr-collection__card-img--hover"
                  loading="lazy"
                  decoding="async" />
              `}
              ${imgSrc ? `
                <img
                  src="${imgSrc}"
                  width="auto"
                  height="auto"
                  alt="${this.escapeHtml(imgAlt)}"
                  class="vr-collection__card-img"
                  loading="lazy"
                  decoding="async" />
              ` : `
                <svg class="vr-collection__card-img vr-collection__card-img--placeholder" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="none"><rect width="100" height="100" fill="#f0f0f0"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="12" fill="#aaa">No Image</text></svg>
              `}
              </figure>
              <a href="/products/${product.handle}" class="vr-collection__buy-btn">BUY YOUR MACHINE</a>
          </div>
          <div class="vr-collection__card-info">
            <h2 class="vr-collection__card-title">${this.escapeHtml(product.title)}</h2>
            <span class="vr-collection__card-price">${priceText}</span>
          </div>
        </article>
      `;
    }).join('');

    this.grid.innerHTML = cardsHtml;
  }

  renderPagination(currentPage, totalPages) {
    if (!this.paginationContainer) return;

    if (totalPages <= 1) {
      this.paginationContainer.innerHTML = '';
      return;
    }

    let partsHtml = '';
    for (let i = 1; i <= totalPages; i++) {
      if (i === currentPage) {
        partsHtml += `<span class="vr-pagination__num is-active" aria-current="page">${i}</span>`;
      } else {
        partsHtml += `<a href="#" class="vr-pagination__num" data-page="${i}">${i}</a>`;
      }
    }

    const prevButton = currentPage > 1 ? `
      <a href="#" class="vr-pagination__btn vr-pagination__btn--prev" data-page="${currentPage - 1}" aria-label="Previous Page">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </a>
    ` : '';

    const nextButton = currentPage < totalPages ? `
      <a href="#" class="vr-pagination__btn vr-pagination__btn--next" data-page="${currentPage + 1}" aria-label="Next Page">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </a>
    ` : '';

    this.paginationContainer.innerHTML = `
      <div class="vr-collection__pagination" id="vr-pagination">
        ${prevButton}
        <div class="vr-pagination__numbers">
          ${partsHtml}
        </div>
        ${nextButton}
      </div>
    `;
  }

  updateCount(count) {
    if (this.countElement) {
      this.countElement.textContent = `${count} ${count === 1 ? 'product' : 'products'}`;
    }
  }

  syncUI(state) {
    // 1. Availability dropdown values: 'in-stock', 'out-of-stock', 'all'
    const availVal = state.getParam('availability') || 'all';
    this.updateDropdownOption('availability', availVal);
    this.updateSelectElement('availability', availVal);

    // 2. Price range values
    const priceVal = state.getParam('price') || 'all';
    this.updateDropdownOption('price', priceVal);
    this.updateSelectElement('price', priceVal);

    // 3. Sort values
    const sortVal = state.getParam('sort') || 'featured';
    this.updateDropdownOption('sort', sortVal);
    this.updateSelectElement('sort', sortVal);
  }

  updateDropdownOption(dropdownType, activeValue) {
    const dropdown = document.querySelector(`.vr-custom-dropdown[data-dropdown="${dropdownType}"]`);
    if (!dropdown) return;

    const options = dropdown.querySelectorAll('.vr-custom-dropdown__option');
    options.forEach(option => {
      const val = option.getAttribute('data-value');
      if (val === activeValue) {
        option.classList.add('is-selected');
        // Update trigger text
        const triggerText = dropdown.querySelector('.vr-custom-dropdown__selected-text');
        if (triggerText) {
          triggerText.textContent = option.textContent.trim();
        }
      } else {
        option.classList.remove('is-selected');
      }
    });
  }

  updateSelectElement(selectType, activeValue) {
    const select = document.querySelector(`select[data-mobile-filter="${selectType}"]`);
    if (select) {
      select.value = activeValue;
    }
  }
}

// 5. Dependency Inversion / Open-Closed Principle: Orchestrator Controller
class CollectionController {
  constructor(state, api, dom, debouncer) {
    this.state = state;
    this.api = api;
    this.dom = dom;
    this.debouncer = debouncer;

    // Get parameters from attributes
    const container = document.querySelector('.vr-collection');
    this.collectionHandle = container ? container.getAttribute('data-collection-handle') : 'all';
    this.limit = container ? parseInt(container.getAttribute('data-products-per-page'), 10) : 9;
    if (isNaN(this.limit)) this.limit = 9;

    this.allProducts = [];
    this.init();
  }

  async init() {
    this.dom.harvestPreRenderedBadges();
    this.dom.showLoading();
    try {
      this.allProducts = await this.api.fetchCollectionProducts(this.collectionHandle);
      this.registerEventListeners();
      
      // Load initial state and render
      this.state.syncFromUrl();
      this.dom.syncUI(this.state);
      this.processAndRender();
    } catch (err) {
      console.error('Initialization failed:', err);
    } finally {
      this.dom.hideLoading();
    }
  }

  registerEventListeners() {
    // 1. Desktop custom dropdowns option click
    const options = document.querySelectorAll('.vr-custom-dropdown__option');
    options.forEach(option => {
      option.addEventListener('click', (e) => {
        const dropdown = option.closest('.vr-custom-dropdown');
        if (!dropdown) return;

        const dropdownType = dropdown.getAttribute('data-dropdown');
        const val = option.getAttribute('data-value');

        this.handleFilterChange(dropdownType, val);
      });
    });

    // 2. Mobile select elements change
    const selects = document.querySelectorAll('select[data-mobile-filter]');
    selects.forEach(select => {
      select.addEventListener('change', (e) => {
        const filterType = select.getAttribute('data-mobile-filter');
        const val = select.value;

        this.handleFilterChange(filterType, val);
      });
    });

    // 3. Reset filters button click
    const clearBtn = document.querySelector('.vr-collection__drawer-btn-clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', (e) => {
        this.state.clearAll();
        this.debouncer.execute(() => this.triggerUpdate());
      });
    }

    // 4. Pagination links ajax load (via event delegation)
    if (this.dom.paginationContainer) {
      this.dom.paginationContainer.addEventListener('click', (e) => {
        const paginationLink = e.target.closest('a');
        if (!paginationLink) return;

        e.preventDefault();
        const page = parseInt(paginationLink.getAttribute('data-page'), 10);
        if (!isNaN(page)) {
          this.state.setPage(page);
          this.triggerUpdate();

          // Scroll to collection top smoothly
          const collectionEl = document.querySelector('.vr-collection');
          if (collectionEl) {
            collectionEl.scrollIntoView({ behavior: 'smooth' });
          }
        }
      });
    }

    // 5. Browser history back/forward navigation
    window.addEventListener('popstate', () => {
      this.state.syncFromUrl();
      this.dom.syncUI(this.state);
      this.processAndRender();
    });
  }

  handleFilterChange(type, value) {
    if (type === 'availability') {
      this.state.setFilter('availability', value);
    } else if (type === 'price') {
      this.state.setFilter('price', value);
    } else if (type === 'sort') {
      this.state.setFilter('sort', value);
    }

    this.debouncer.execute(() => this.triggerUpdate());
  }

  triggerUpdate() {
    this.dom.showLoading();
    this.processAndRender();
    
    // Update Browser URL Query string
    const queryString = this.state.buildQueryString();
    const newUrl = `${window.location.pathname}${queryString}`;
    window.history.pushState({}, '', newUrl);
    
    this.dom.syncUI(this.state);
    this.dom.hideLoading();
  }

  processAndRender() {
    // 1. Filter
    let filtered = [...this.allProducts];

    // Filter by availability
    const availabilityFilter = this.state.getParam('availability');
    if (availabilityFilter === 'in-stock') {
      filtered = filtered.filter(p => p.variants.some(v => v.available));
    } else if (availabilityFilter === 'out-of-stock') {
      filtered = filtered.filter(p => !p.variants.some(v => v.available));
    }

    // Filter by price range
    const priceFilter = this.state.getParam('price');
    if (priceFilter && priceFilter !== 'all') {
      filtered = filtered.filter(product => {
        const price = product.variants.length > 0 ? parseFloat(product.variants[0].price) : 0;
        if (priceFilter === 'under-250') {
          return price < 250;
        } else if (priceFilter === '250-300') {
          return price >= 250 && price <= 300;
        } else if (priceFilter === 'over-300') {
          return price >= 300;
        }
        return true;
      });
    }

    // 2. Sort
    const sortVal = this.state.getParam('sort');
    if (sortVal === 'az') {
      filtered.sort((a, b) => a.title.localeCompare(b.title));
    } else if (sortVal === 'za') {
      filtered.sort((a, b) => b.title.localeCompare(a.title));
    } else if (sortVal === 'low-high') {
      filtered.sort((a, b) => {
        const priceA = a.variants.length > 0 ? parseFloat(a.variants[0].price) : 0;
        const priceB = b.variants.length > 0 ? parseFloat(b.variants[0].price) : 0;
        return priceA - priceB;
      });
    } else if (sortVal === 'high-low') {
      filtered.sort((a, b) => {
        const priceA = a.variants.length > 0 ? parseFloat(a.variants[0].price) : 0;
        const priceB = b.variants.length > 0 ? parseFloat(b.variants[0].price) : 0;
        return priceB - priceA;
      });
    }

    // 3. Paginate
    const totalCount = filtered.length;
    const totalPages = Math.ceil(totalCount / this.limit);
    let currentPage = this.state.getPage();
    if (currentPage > totalPages) currentPage = totalPages || 1;

    const offset = (currentPage - 1) * this.limit;
    const paginatedProducts = filtered.slice(offset, offset + this.limit);

    // 4. Render
    this.dom.renderProducts(paginatedProducts);
    this.dom.renderPagination(currentPage, totalPages);
    this.dom.updateCount(totalCount);
  }
}

// Instantiate and kick off the module when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const state = new CollectionState();
  const api = new CollectionAPI();
  const dom = new CollectionDOM();
  const debouncer = new CollectionDebouncer(300);

  // Instantiating orchestrator
  new CollectionController(state, api, dom, debouncer);
});
